import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Facebook icon component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconFacebook extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_13883_3182)">
            <path
              d="M21.3 23.8H16.2C15.8 23.8 15.4 23.5 15.4 23V14.9C15.4 14.5 15.7 14.1 16.2 14.1H17.4C17.6 14.1 17.7 14 17.7 13.8V12.5C17.7 12.3 17.6 12.2 17.4 12.2H16.3C15.9 12.2 15.5 11.9 15.5 11.4V9.9C15.5 7.7 16.9 7.7 17.6 7.7H18.4C18.5 7.7 18.5 7.7 18.6 7.6C18.7 7.5 18.7 7.4 18.7 7.4V6.1C18.7 6 18.6 5.9 18.6 5.9C18.6 5.9 18.5 5.8 18.4 5.8H16.5C14.7 5.7 13.2 7.1 13.1 8.9C13.1 9.1 13.1 9.2 13.1 9.4V11.5C13.1 11.9 12.8 12.3 12.4 12.3H11.3C11.2 12.3 11.1 12.4 11.1 12.4C11.1 12.4 11 12.5 11 12.6V13.9C11 14.1 11.1 14.2 11.3 14.2H12.4C12.8 14.2 13.1 14.5 13.1 15V23.1C13.1 23.5 12.8 23.9 12.4 23.9H2.5C1.1 23.9 0 22.8 0 21.4V2.5C0 1.1 1.1 0 2.5 0H21.4C22.8 0 23.9 1.1 23.9 2.5V21.4C23.8 22.7 22.7 23.8 21.3 23.8ZM16.9 22.3H21.3C21.8 22.3 22.3 21.9 22.3 21.3V2.5C22.3 2 21.9 1.5 21.3 1.5H2.5C2 1.5 1.5 1.9 1.5 2.5V21.4C1.5 21.9 1.9 22.4 2.5 22.4H11.6V15.8H11.3C10.3 15.8 9.5 15 9.5 14V12.7C9.5 11.7 10.4 10.9 11.3 10.9H11.6V9.5C11.6 9.3 11.6 9.1 11.6 8.8C11.7 6.2 13.9 4.1 16.5 4.3H18.4C18.9 4.3 19.4 4.5 19.7 4.9C20 5.3 20.2 5.7 20.2 6.2V7.4C20.2 7.9 20 8.3 19.7 8.7C19.4 9 18.9 9.2 18.4 9.2H17.6C17.5 9.2 17.2 9.2 17.1 9.2C17.1 9.2 17 9.3 17 9.9V10.8H17.4C18.4 10.8 19.2 11.6 19.2 12.6V13.9C19.2 14.9 18.4 15.7 17.4 15.7H16.9V22.3Z"
            />
          </g>
          <defs>
            <clipPath id="clip0_13883_3182">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-facebook') === undefined) {
  customElements.define('cat-icon-facebook', BlocksIconFacebook);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-facebook': BlocksIconFacebook;
  }
}
